import type { LeaguePublic, Schemas } from "@softmax/observatory-client/types";

import { backendFetchResponse } from "@softmax/observatory-client/transport";
import { type GamePageContext } from "../../game-types";

type LeagueResolution =
  | {
      readonly status: "ok";
      readonly league: LeaguePublic;
    }
  | {
      readonly status: "not-found";
    }
  | {
      readonly status: "unavailable";
    };

export async function readGameLeague({
  signal,
  id,
  isPublic,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly id: string;
  readonly isPublic: boolean;
}): Promise<LeagueResolution> {
  const response = await backendFetchResponse(
    "/api/observatory",
    `/v2/leagues/${encodeURIComponent(id)}`,
    {
      headers: { "X-Use-Elevated-Privileges": "false" },
      credentials: isPublic ? "omit" : "same-origin",
      signal: signal ?? null,
    },
  );

  if (response.status === 404 || response.status === 403)
    return { status: "not-found" };

  if (!response.ok) return { status: "unavailable" };

  return { status: "ok", league: await response.json() };
}

export async function resolveGameLeague({
  signal,
  publicAddress,
  context = null,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly publicAddress: string;
  readonly context?: GamePageContext | null;
}): Promise<LeagueResolution> {
  const raw = publicAddress.replace(/^\/?unlisted\//, "").replace(/^\//, "");
  const [slug, leagueSlug, ...tail] = raw.split("/");

  if (!slug || tail.length) return { status: "not-found" };

  const sameGame =
    context && (slug === context.address.slug || slug === context.corpusSlug);

  if (
    sameGame &&
    context.league &&
    (leagueSlug ?? null) === context.league.requestedSlug
  ) {
    return readGameLeague({
      signal,
      id: context.league.id,
      isPublic: context.league.public,
    });
  }

  if (sameGame && context.mode === "upload") return { status: "not-found" };

  return readRoutedGameLeague({
    signal,
    slug: sameGame ? context.address.slug : slug,
    leagueSlug,
  });
}

async function readRoutedGameLeague({
  signal,
  slug,
  leagueSlug,
}: {
  readonly signal: AbortSignal | undefined;
  readonly slug: string;
  readonly leagueSlug: string | undefined;
}): Promise<LeagueResolution> {
  const query = new URLSearchParams();

  if (leagueSlug) query.set("league", leagueSlug);

  const response = await backendFetchResponse(
    "/api/observatory",
    `/v2/game-addresses/${encodeURIComponent(slug)}/routing${query.size ? `?${query}` : ""}`,
    {
      headers: { "X-Use-Elevated-Privileges": "false" },
      credentials: "same-origin",
      signal: signal ?? null,
    },
  );

  if (response.status === 404 || response.status === 403)
    return { status: "not-found" };

  if (!response.ok) return { status: "unavailable" };

  const resolution: Schemas["GameAddressRouting"] = await response.json();
  return resolution.league
    ? readGameLeague({
        signal,
        id: resolution.league.id,
        isPublic: resolution.league.public,
      })
    : { status: "not-found" };
}
